import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export interface ReportShareTokenRow {
  id: string;
  cleaning_task_id: string;
  property_id: string;
  token: string;
  short_code: string | null;
  created_at: string;
  revoked_at: string | null;
}

export function useReportShareToken(cleaningTaskId: string | undefined) {
  return useQuery({
    queryKey: ["report-share-token", cleaningTaskId],
    queryFn: async (): Promise<ReportShareTokenRow | null> => {
      if (!cleaningTaskId) return null;
      const { data, error } = await supabase
        .from("report_share_tokens")
        .select("id, cleaning_task_id, property_id, token, short_code, created_at, revoked_at")
        .eq("cleaning_task_id", cleaningTaskId)
        .maybeSingle();
      if (error) throw error;
      return data as ReportShareTokenRow | null;
    },
    enabled: !!cleaningTaskId,
  });
}

export function useEnsureReportShareToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cleaningTaskId: string) => {
      const { data, error } = await supabase.rpc("ensure_report_share_token", {
        p_cleaning_task_id: cleaningTaskId,
      });
      if (error) throw error;
      return data as ReportShareTokenRow | null;
    },
    onSuccess: (data, cleaningTaskId) => {
      if (data) {
        queryClient.setQueryData(["report-share-token", cleaningTaskId], data);
      }
      queryClient.invalidateQueries({ queryKey: ["report-share-token", cleaningTaskId] });
    },
  });
}

export function useRevokeReportShareToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      const { error } = await supabase
        .from("report_share_tokens")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", tokenId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-share-token"] });
    },
  });
}
